import type { EditorContextProps } from '@tunasong/editor'
import { graphHooks } from '@tunasong/graph-lib/react'
import { Delete, Performance } from '@tunasong/icons'
import type { Note, NoteType } from '@tunasong/models'
import { isCollabEntity, isNote, shortUuid } from '@tunasong/models'
import { createCollaborativeDoc } from '@tunasong/sync-lib'
import { usePlugins } from '@tunasong/plugin-lib'
import type { Entity, Persisted } from '@tunasong/schemas'
import { isPersisted } from '@tunasong/schemas'
import { ConfirmDialog, Button, ButtonGroup } from '@tunasong/ui-lib'
import { DynamicEntityEditor, LazyCollabEditor } from '@tunasong/plugin-collab-yjs'
import { useCallback, useState } from 'react'
import styles from './note-editor.module.css'
import invariant from 'tiny-invariant'
import { useCurrentUser } from '@tunasong/redux'
export interface NoteEditorProps extends Omit<EditorContextProps, 'element'> {
  element: Note | null
  controls?: boolean
  /** If parent is provided, the note is treated as a draft that will be
   *  lazily created on first content via LazyCollabEditor. */
  parent?: Persisted<Entity>
  /** Extra metadata for lazy-created notes */
  metadata?: Record<string, unknown>
  /** Create as private entity (only applies to lazy-created notes) */
  isPrivate?: boolean
}

export const NoteEditor = (props: NoteEditorProps) => {
  const { element, controls = false, parent, metadata, isPrivate = true, ...restProps } = props
  const [showDelete, setShowDelete] = useState(false)
  const notePlugins = usePlugins('note')
  const updateEntity = graphHooks.useEntityUpdate<Note>({ debounceDelay: 1 * 1000 })

  const handleType = useCallback(
    (noteType: NoteType) => () => {
      if (!isPersisted(element)) {
        return
      }
      updateEntity(element.id, { noteType })
    },
    [element, updateEntity]
  )

  const trash = graphHooks.useEntityTrash(element as never)
  const handleDelete = () => {
    if (!(isNote(element) && isPersisted(element))) {
      return
    }
    setShowDelete(false)
    trash()
  }

  const handleShowDelete = () => setShowDelete(true)
  const handleHideDelete = () => setShowDelete(false)

  const { userId } = useCurrentUser()
  // Create draft entity once on mount — useState initializer keeps it stable across
  // re-renders so the editor isn't swapped out when the entity gets persisted.
  const [draftEntity] = useState<Persisted<Note> | null>(() => {
    if (element || !parent?.id) {
      return null
    }
    const id = shortUuid()
    const { yDoc } = createCollaborativeDoc({ id, nodes: [{ type: 'line', children: [{ text: '' }] }] })
    const now = new Date().toISOString()
    const el = {
      type: 'note',
      id,
      parentId: parent.id,
      userId,
      metadata,
      createdAt: now,
      updatedAt: now,
      yDoc,
    } as Persisted<Note>
    invariant(isCollabEntity(el) && isPersisted(el), 'Draft entity must be a collab entity with yDoc')
    return el
  })

  if (!notePlugins) {
    return null
  }

  // When the component mounted with an existing element that later becomes null
  // (e.g., entity list re-fetch, track deletion), draftEntity stays null because
  // useState initializers run only once. Return null and let the parent re-mount.
  if (!element && !(draftEntity && parent)) {
    return null
  }

  // Draft editor takes priority — once mounted, keep it alive even after persist
  // to avoid remounting the editor and losing focus.
  // Falls back to DynamicEntityEditor for both persisted and non-persisted elements
  // (e.g., inline draft templates from entity-summary).
  const editor = draftEntity && parent ? (
    <LazyCollabEditor {...restProps} plugins={notePlugins} draft={draftEntity} parent={parent} isPrivate={isPrivate} />
  ) : element ? (
    <DynamicEntityEditor {...restProps} element={element as Entity} plugins={notePlugins} gutter={false} />
  ) : null

  const noteType = (isNote(element) ? element.noteType : undefined) ?? 'general'

  return !controls ? (
    editor
  ) : (
    <div className={styles.container}>
      <ButtonGroup variant="text" size="small" aria-label="Note Type">
        <Button title="Performance Note" onClick={handleType('performance')}>
          <Performance
            style={{
              color:
                noteType === 'performance' ? 'var(--tuna-palette-primary-main)' : 'var(--tuna-palette-action-focus)',
            }}
          />
        </Button>
        <Button title="Delete Note  " onClick={handleShowDelete}>
          <Delete style={{ color: 'var(--tuna-palette-action-focus)' }} />
        </Button>
      </ButtonGroup>
      <div className={styles.spacer} />
      {editor}
      <ConfirmDialog
        open={showDelete}
        title="Delete note"
        text={`Really delete ${noteType} note?`}
        onCancel={handleHideDelete}
        onConfirm={handleDelete}
      />
    </div>
  )
}

export default NoteEditor
